import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { BookOpen, MessageSquare, ChevronRight, MapPin, ArrowLeft, GraduationCap } from "lucide-react";
import { getUniversityTheme } from "../lib/universityThemes";
import { UniCrest } from "../components/UniCrest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const COUNTRY_SLUGS = {
  jordan: { name: "Jordan", flag: "\uD83C\uDDEF\uD83C\uDDF4", code: "JO" },
  palestine: { name: "Palestine", flag: "\uD83C\uDDF5\uD83C\uDDF8", code: "PS" },
  georgia: { name: "Georgia", flag: "\uD83C\uDDEC\uD83C\uDDEA", code: "GE" },
  italy: { name: "Italy", flag: "\uD83C\uDDEE\uD83C\uDDF9", code: "IT" },
};

function UniCard({ uni, navigate, featured }) {
  const inactive = uni.is_active === false;
  const theme = getUniversityTheme(uni.external_id);
  return (
    <Card
      className={`group overflow-hidden transition-all duration-300 ${inactive ? "opacity-50 grayscale cursor-not-allowed" : "cursor-pointer hover:border-primary/50 hover:shadow-lg hover:-translate-y-0.5"}`}
      style={theme ? { borderTop: `4px solid ${theme.primaryColor}` } : undefined}
      onClick={() => { if (!inactive) navigate(`/university/${uni.external_id}`); }}
      data-testid={`country-uni-card-${uni.external_id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-background text-primary" style={theme ? { boxShadow: `0 2px 8px ${theme.primaryColor}33` } : undefined}>
            {theme ? <UniCrest theme={theme} size={26} /> : <GraduationCap className="h-6 w-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold mb-1 truncate">{uni.name}</h3>
            <p className="text-sm text-muted-foreground mb-2">{uni.city}, {uni.country}</p>
            {(uni.course_count > 0 || uni.mcq_count > 0) && (
              <div className="flex items-center gap-3 mb-2 text-xs">
                {uni.course_count > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                    <BookOpen className="h-3 w-3" />{uni.course_count} Courses
                  </span>
                )}
                {uni.mcq_count > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] font-medium">
                    <MessageSquare className="h-3 w-3" />
                    {uni.mcq_count >= 1000 ? `${(uni.mcq_count / 1000).toFixed(1)}K` : uni.mcq_count} MCQs
                  </span>
                )}
              </div>
            )}
            {inactive ? (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-muted text-muted-foreground text-xs font-semibold uppercase tracking-wide">Coming Soon</span>
            ) : (
              <div className="flex items-center text-sm text-primary font-medium group-hover:gap-2 transition-all">
                Explore Courses<ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CountryLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const info = COUNTRY_SLUGS[(slug || "").toLowerCase()];

  useEffect(() => {
    if (!info) { navigate("/", { replace: true }); return; }
    fetch(`${API}/universities`)
      .then((r) => r.json())
      .then((d) => setUniversities(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!info) return null;
  const local = universities.filter((u) => u.country === info.name);
  const others = universities.filter((u) => u.country !== info.name);

  return (
    <div className="min-h-screen bg-background" data-testid="country-landing-page">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="country-back-home"
        >
          <ArrowLeft className="h-4 w-4" /> All universities
        </button>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
            <MapPin className="h-3.5 w-3.5" /> Studying in {info.name}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3" data-testid="country-title">
            {info.flag} Universities in {info.name}
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl">
            Exam-style question banks, video lessons and full course coverage for medical,
            dental, pharmacy and nursing programs in {info.name}.
          </p>
          <button
            onClick={() => navigate(`/free-quiz?country=${info.code}`)}
            className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-gradient-to-r from-[#2563EB] to-[#1E40AF] text-white px-6 py-4 shadow-xl shadow-blue-600/25 hover:scale-[1.02] transition-transform"
            data-testid="country-free-quiz-cta"
          >
            <span className="text-left">
              <span className="block text-lg font-bold">Try 10 Free Questions</span>
              <span className="block text-xs text-blue-100">Real exam questions · no sign-up needed</span>
            </span>
            <ChevronRight className="h-5 w-5 shrink-0" />
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => <div key={i} className="h-44 rounded-xl bg-muted animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14" data-testid="country-local-grid">
              {local.map((uni) => <UniCard key={uni.external_id} uni={uni} navigate={navigate} featured />)}
            </div>

            <h2 className="text-lg font-semibold text-muted-foreground mb-5" data-testid="country-others-heading">
              More universities on ClassMate
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="country-others-grid">
              {others.map((uni) => <UniCard key={uni.external_id} uni={uni} navigate={navigate} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
